import { useQuery } from "@tanstack/react-query";
import { BellRing, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { get } from "../../api/client";
import type { Conversation, Page } from "../../api/types";
import { useCan, useSession } from "../../auth/session";
import { Button } from "../../components/ui/Button";
import { toast } from "../../components/ui/toast";
import { formatPhone } from "../../lib/format";
import { appInBackground, newInboundTexts, dismissPrompt, enableNotifications, showNotification, useNotifyState } from "../../lib/notifications";
import { useVoice } from "../../voice/store";
import { useNewVoicemails } from "../voicemail/useNewVoicemails";
import { unreadMessagesKey, useRefreshBadgeOnChange } from "../../layout/useBadgeCounts";

const MSG_READ = ["messages.read.own", "messages.read.team", "messages.read.organization"];

/** Raises desktop notifications for incoming calls and new texts while the app isn't in front. */
export function NotificationsWatcher() {
  const { canAny } = useCan();
  return (
    <>
      {canAny("calls.receive") && <CallNotifications />}
      {canAny(...MSG_READ) && <MessageNotifications />}
      {canAny("voicemail.read", "voicemail.read.organization") && <VoicemailNotifications />}
    </>
  );
}

function CallNotifications() {
  const incoming = useVoice((s) => s.incoming);
  const shown = useRef(new Map<string, Notification>());
  useEffect(() => {
    const live = new Set(incoming.map((c) => c.callSid));
    for (const call of incoming) {
      if (shown.current.has(call.callSid) || !appInBackground()) continue;
      const n = showNotification("Incoming call", { body: `${formatPhone(call.from)} is calling — click to answer`, tag: `call-${call.callSid}`, sticky: true });
      if (n) shown.current.set(call.callSid, n);
    }
    // Answered, declined, canceled or missed: take the notification down.
    for (const [sid, n] of shown.current) {
      if (!live.has(sid)) { n.close(); shown.current.delete(sid); }
    }
  }, [incoming]);
  useEffect(() => () => { for (const n of shown.current.values()) n.close(); }, []);
  return null;
}

function MessageNotifications() {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const path = useRef(location.pathname);
  useEffect(() => { path.current = location.pathname; }, [location.pathname]);
  // Newest message time already seen per conversation; null until the first load sets the baseline.
  const seen = useRef<Map<string, string> | null>(null);
  const q = useQuery({
    queryKey: ["conversations", "notifications"],
    queryFn: () => get<Page<Conversation>>("/conversations?view=all&pageSize=30"),
    refetchInterval: 10_000,
    refetchIntervalInBackground: true,
  });
  useRefreshBadgeOnChange(q.data, unreadMessagesKey);

  useEffect(() => {
    if (!q.data) return;
    const { fresh, seen: next } = newInboundTexts(seen.current, q.data.items, session?.membershipId ?? null);
    seen.current = next;
    for (const c of fresh) {
      const who = c.contact?.name ?? formatPhone(c.externalNumber);
      const body = c.lastMessagePreview ?? "New message";
      const open = () => navigate(`/messages/${c.id}`);
      if (appInBackground()) {
        showNotification(`New text from ${who}`, { body, tag: `conversation-${c.id}`, onClick: open });
      } else if (path.current !== `/messages/${c.id}`) {
        toast.info(`New text from ${who}: ${body}`);
      }
    }
  }, [q.data, session?.membershipId, navigate]);
  return null;
}

function VoicemailNotifications() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = useRef(location.pathname);
  useEffect(() => { path.current = location.pathname; }, [location.pathname]);
  const seen = useRef<Set<string> | null>(null); // null until the first load sets the baseline
  const q = useNewVoicemails(true);
  useEffect(() => {
    if (!q.data) return;
    const first = seen.current === null;
    const known = seen.current ?? new Set<string>();
    for (const v of q.data.items) {
      if (known.has(v.id)) continue;
      known.add(v.id);
      if (first) continue;
      const who = v.contact?.name ?? formatPhone(v.from);
      if (appInBackground()) {
        showNotification(`New voicemail from ${who}`, { body: v.phoneNumber ? `Left on ${v.phoneNumber.label}` : "Click to listen", tag: `voicemail-${v.id}`, onClick: () => navigate("/voicemail") });
      } else if (path.current !== "/voicemail") {
        toast.info(`New voicemail from ${who}`);
      }
    }
    seen.current = known;
  }, [q.data, navigate]);
  return null;
}

/** One-time invitation to turn on notifications (the browser only asks after a click). */
export function NotificationsPrompt() {
  const { canAny } = useCan();
  const [state, dismissed] = useNotifyState().split("|");
  if (state !== "ask" || dismissed === "1" || !canAny("calls.receive", "voicemail.read", ...MSG_READ)) return null;
  return (
    <div role="region" aria-label="Notifications" className="glass flex flex-wrap items-center gap-3 rounded-2xl px-4 py-2.5 text-sm">
      <BellRing className="h-4 w-4 shrink-0 text-accent-600" aria-hidden="true" />
      <p className="min-w-0 flex-1 text-default">Get a desktop notification when a call or text comes in while AtlanTech Solution is in the background.</p>
      <Button size="sm" variant="primary" onClick={async () => {
        const s = await enableNotifications();
        if (s === "on") toast.success("Notifications are on");
        else if (s === "blocked") toast.error("Notifications are blocked for this site in your browser settings");
      }}>Turn on</Button>
      <button className="focus-ring rounded-lg p-1 text-muted hover:bg-white/70" aria-label="Not now" onClick={dismissPrompt}><X className="h-4 w-4" /></button>
    </div>
  );
}
